import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, MapPin, Star, Edit2, Trash2, Heart, Calendar, Music, Disc3, Users, ChevronRight, X, Clock, Facebook, Instagram, Twitter, Globe, Check, XCircle, Loader2, Settings, Lock, Eye, EyeOff, BarChart3, Gamepad2, Ticket, Copy, Camera, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import TopRequestedDances from '../components/TopRequestedDances';
import ImageUpload from '../components/ImageUpload';
import { NotificationSettings } from '../components/NotificationSettings';

import { API_URL } from '../utils/api';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myClubs, setMyClubs] = useState([]);
  const [myDJs, setMyDJs] = useState([]);
  const [myArtists, setMyArtists] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-clubs');
  
  // Instructor state
  const [instructorProfile, setInstructorProfile] = useState(null);
  const [showInstructorForm, setShowInstructorForm] = useState(false);
  const [instructorFormData, setInstructorFormData] = useState({
    name: '',
    bio: '',
    specialties: [],
    specialtiesText: '',
    email: '',
    phone: '',
    years_experience: '',
    image_url: '',
    social_links: { facebook: '', instagram: '', twitter: '', website: '' }
  });
  const [instructorSubmitting, setInstructorSubmitting] = useState(false);
  const [myLinkRequests, setMyLinkRequests] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [selectedClubForLink, setSelectedClubForLink] = useState('');
  const [clubLinkRequests, setClubLinkRequests] = useState({});  // {club_id: [requests]}
  
  // Password change state
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Photo Wall state
  const [photoWallModalClub, setPhotoWallModalClub] = useState(null);
  const [photoWallSession, setPhotoWallSession] = useState(null);
  const [photoWallLoading, setPhotoWallLoading] = useState(false);
  const [photoWallSessionName, setPhotoWallSessionName] = useState('');

  useEffect(() => {
    if (user) {
      fetchData();
    }
    // Check for tab query param
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [clubsRes, djsRes, artistsRes, eventsRes, favoritesRes, instructorRes, allClubsRes] = await Promise.all([
        fetch(`${API_URL}/api/my-clubs`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/my-djs`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/artists/my/profiles`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/my-events`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/favorites`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/instructors/me/profile`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/clubs`)
      ]);

      if (clubsRes.ok) {
        const clubs = await clubsRes.json();
        setMyClubs(clubs);
        // Fetch link requests for each club
        const linkRequestsObj = {};
        for (const club of clubs) {
          const reqRes = await fetch(`${API_URL}/api/clubs/${club.club_id}/link-requests`, { credentials: 'include', headers });
          if (reqRes.ok) {
            linkRequestsObj[club.club_id] = await reqRes.json();
          }
        }
        setClubLinkRequests(linkRequestsObj);
      }
      if (djsRes.ok) {
        setMyDJs(await djsRes.json());
      }
      if (artistsRes.ok) {
        setMyArtists(await artistsRes.json());
      }
      if (eventsRes.ok) {
        setMyEvents(await eventsRes.json());
      }
      if (favoritesRes.ok) {
        setFavorites(await favoritesRes.json());
      }
      if (instructorRes.ok) {
        const profile = await instructorRes.json();
        setInstructorProfile(profile);
        // Fetch link requests
        if (profile) {
          const reqRes = await fetch(`${API_URL}/api/instructors/me/link-requests`, { credentials: 'include', headers });
          if (reqRes.ok) {
            setMyLinkRequests(await reqRes.json());
          }
        }
      }
      if (allClubsRes.ok) {
        setAllClubs(await allClubsRes.json());
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${clubId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        setMyClubs(myClubs.filter(c => c.club_id !== clubId));
        toast.success('Club deleted successfully');
      } else {
        toast.error('Failed to delete club');
      }
    } catch (error) {
      toast.error('Error deleting club');
    }
  };

  const handleDeleteDJ = async (djId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/djs/${djId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        setMyDJs(myDJs.filter(d => d.dj_id !== djId));
        toast.success('DJ profile deleted');
      } else {
        toast.error('Failed to delete DJ profile');
      }
    } catch (error) {
      toast.error('Error deleting DJ profile');
    }
  };

  const removeFavorite = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/favorites/${clubId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        setFavorites(favorites.filter(c => c.club_id !== clubId));
        toast.success('Removed from favorites');
      }
    } catch (error) {
      toast.error('Error removing favorite');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      console.log('[DeleteEvent] Attempting to delete:', eventId);
      
      // Use XMLHttpRequest to avoid response body being consumed by platform scripts
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${API_URL}/api/events/${eventId}`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = function() {
          let data = {};
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            data = { detail: xhr.responseText };
          }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        };
        xhr.onerror = function() {
          reject(new Error('Network request failed'));
        };
        xhr.send();
      });
      
      console.log('[DeleteEvent] Response:', result);
      
      if (result.ok) {
        setMyEvents(myEvents.filter(e => e.event_id !== eventId));
        toast.success('Event deleted');
      } else {
        toast.error(result.data.detail || `Failed to delete event (${result.status})`);
      }
    } catch (error) {
      console.error('[DeleteEvent] Error:', error);
      toast.error('Error deleting event: ' + error.message);
    }
  };

  // ===== PHOTO WALL FUNCTIONS =====
  
  const openPhotoWallModal = async (club) => {
    setPhotoWallModalClub(club);
    setPhotoWallLoading(true);
    setPhotoWallSessionName(club.name + ' Photo Wall');
    
    try {
      const response = await fetch(`${API_URL}/api/clubs/${club.slug || club.club_id}/photo-wall/session`);
      const data = await response.json();
      if (data.active && data.session) {
        setPhotoWallSession(data.session);
      } else {
        setPhotoWallSession(null);
      }
    } catch (error) {
      console.error('Error fetching photo wall session:', error);
      setPhotoWallSession(null);
    } finally {
      setPhotoWallLoading(false);
    }
  };
  
  const startPhotoWallSession = async () => {
    if (!photoWallModalClub || !photoWallSessionName.trim()) return;
    
    setPhotoWallLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${photoWallModalClub.club_id}/photo-wall/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_name: photoWallSessionName.trim(),
          moderation_enabled: true,
          auto_approve: true
        })
      });
      
      if (response.ok) {
        const session = await response.json();
        setPhotoWallSession(session);
        toast.success('Photo Wall session started!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start session');
      }
    } catch (error) {
      toast.error('Error starting session');
    } finally {
      setPhotoWallLoading(false);
    }
  };
  
  const endPhotoWallSession = async () => {
    if (!photoWallModalClub || !photoWallSession) return;
    
    setPhotoWallLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${photoWallModalClub.club_id}/photo-wall/sessions/${photoWallSession.session_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPhotoWallSession(null);
        toast.success('Photo Wall session ended');
      } else {
        toast.error('Failed to end session');
      }
    } catch (error) {
      toast.error('Error ending session');
    } finally {
      setPhotoWallLoading(false);
    }
  };

  // ===== INSTRUCTOR FUNCTIONS =====
  
  const handleInstructorSubmit = async (e) => {
    e.preventDefault();
    if (!instructorFormData.name || !instructorFormData.bio) {
      toast.error('Please fill in name and bio');
      return;
    }

    setInstructorSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...instructorFormData,
        years_experience: instructorFormData.years_experience ? parseInt(instructorFormData.years_experience) : null,
        specialties: instructorFormData.specialties.filter(s => s.trim())
      };
      
      const url = instructorProfile 
        ? `${API_URL}/api/instructors/me/profile`
        : `${API_URL}/api/instructors/register`;
      const method = instructorProfile ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setInstructorProfile(data);
        setShowInstructorForm(false);
        toast.success(instructorProfile ? 'Profile updated!' : 'Registered as instructor!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      toast.error('Error saving profile');
    } finally {
      setInstructorSubmitting(false);
    }
  };

  const handleRequestClubLink = async () => {
    if (!selectedClubForLink) {
      toast.error('Please select a club');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/instructors/me/request-club-link/${selectedClubForLink}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Link request sent to club owner');
        setSelectedClubForLink('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send request');
      }
    } catch (error) {
      toast.error('Error sending request');
    }
  };

  const handleCancelLinkRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/instructors/me/link-requests/${requestId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Request cancelled');
        fetchData();
      }
    } catch (error) {
      toast.error('Error cancelling request');
    }
  };

  const handleUnlinkFromClub = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/instructors/me/clubs/${clubId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Unlinked from club');
        fetchData();
      }
    } catch (error) {
      toast.error('Error unlinking from club');
    }
  };

  const handleApproveLinkRequest = async (clubId, requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${clubId}/link-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Instructor approved');
        fetchData();
      }
    } catch (error) {
      toast.error('Error approving request');
    }
  };

  const handleRejectLinkRequest = async (clubId, requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${clubId}/link-requests/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Request rejected');
        fetchData();
      }
    } catch (error) {
      toast.error('Error rejecting request');
    }
  };

  const openEditInstructorForm = () => {
    if (instructorProfile) {
      const specialtiesArr = instructorProfile.specialties || [];
      setInstructorFormData({
        name: instructorProfile.name || '',
        bio: instructorProfile.bio || '',
        specialties: specialtiesArr,
        specialtiesText: specialtiesArr.join(', '),
        email: instructorProfile.email || '',
        phone: instructorProfile.phone || '',
        years_experience: instructorProfile.years_experience?.toString() || '',
        image_url: instructorProfile.image_url || '',
        social_links: instructorProfile.social_links || { facebook: '', instagram: '', twitter: '', website: '' }
      });
    }
    setShowInstructorForm(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatEventTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const isEventPast = (dateString) => {
    return new Date(dateString) < new Date();
  };

  // Format event for Facebook posting
  const formatEventForFacebook = (event) => {
    const lines = [];
    lines.push('🎉 LINE DANCE EVENT 🎉');
    lines.push('');
    if (event.title) lines.push(`📌 ${event.title}`);
    if (event.club_name) lines.push(`🏠 ${event.club_name}`);
    if (event.location) lines.push(`📍 ${event.location}`);
    if (event.event_date) lines.push(`📅 ${formatEventDate(event.event_date)}`);
    if (event.event_time) lines.push(`🕐 ${formatEventTime(event.event_time)}`);
    if (event.price) lines.push(`💰 £${typeof event.price === 'number' ? event.price.toFixed(2) : event.price}`);
    if (event.description) {
      lines.push('');
      lines.push(event.description.substring(0, 200) + (event.description.length > 200 ? '...' : ''));
    }
    return lines.join('\n');
  };

  // Copy single event to clipboard
  const copyEventToClipboard = async (event) => {
    try {
      const text = formatEventForFacebook(event);
      await navigator.clipboard.writeText(text);
      toast.success('Event copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Copy all events to clipboard
  const copyAllEventsToClipboard = async () => {
    try {
      if (myEvents.length === 0) {
        toast.error('No events to copy');
        return;
      }
      
      const formattedEvents = myEvents.map(formatEventForFacebook).join('\n\n━━━━━━━━━━━━━━━━━━━━\n\n');
      const header = `📣 UPCOMING LINE DANCE EVENTS (${myEvents.length})\n\n`;
      await navigator.clipboard.writeText(header + formattedEvents);
      toast.success(`${myEvents.length} events copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={14}
        className={i < Math.floor(rating) ? 'fill-violet-500 text-violet-500' : 'text-slate-300'}
      />
    ));
  };

  const ClubCard = ({ club, isOwner = false }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300"
    >
      {isOwner ? (
        // Full-width layout for owners
        <div className="p-6">
          {/* Header: Logo and Club Name across the page */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border border-slate-200">
              <img
                src={club.image_url || 'https://images.unsplash.com/photo-1761858736318-f1fe86aec4db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3VudHJ5JTIwd2VzdGVybiUyMGRhbmNlJTIwY2x1YiUyMGludGVyaW9yJTIwbmVvbnxlbnwwfHx8fDE3NzIwOTgwMjB8MA&ixlib=rb-4.1.0&q=85'}
                alt={club.name}
                className="w-full h-full object-cover"
              />
              {club.is_featured && (
                <Badge className="absolute -top-1 -right-1 bg-violet-500 text-white text-xs px-1.5">Featured</Badge>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/clubs/${club.slug || club.club_id}`}>
                <h3 className="text-xl font-bold text-slate-900 hover:text-violet-600 transition-colors truncate">
                  {club.name}
                </h3>
              </Link>
              <p className="flex items-center text-slate-500 text-sm mt-1">
                <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                <span className="truncate">{club.location || [club.city, club.region, club.country].filter(Boolean).join(', ') || 'Location not specified'}</span>
              </p>
              <div className="flex items-center gap-1 mt-1">
                {renderStars(club.rating)}
              </div>
            </div>
            {/* Edit/Delete buttons on the right */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/dashboard/edit-club/${club.club_id}`)}
                data-testid={`edit-club-${club.club_id}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-club-${club.club_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Club</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{club.name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteClub(club.club_id)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          
          {/* Action buttons spread equally across */}
          <div className="grid grid-cols-3 gap-3">
            {/* DJ Console */}
            {club.dj_live_enabled ? (
              <Link to={`/club/${club.slug || club.club_id}/dj-console`} className="block">
                <Button
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-500 h-12"
                  data-testid={`dj-console-${club.club_id}`}
                >
                  <Disc3 className="w-4 h-4 mr-2" />
                  DJ Console
                </Button>
              </Link>
            ) : (
              <div className="h-12" /> 
            )}
            
            {/* Host Bingo */}
            {club.bingo_enabled ? (
              <Link to={`/bingo/host?club=${club.club_id}`} className="block">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-violet-300 text-violet-700 hover:bg-violet-50 h-12"
                  data-testid={`host-bingo-${club.club_id}`}
                >
                  <Gamepad2 className="w-4 h-4 mr-2" />
                  Host Bingo
                </Button>
              </Link>
            ) : (
              <div className="h-12" />
            )}
            
            {/* Manage Tickets */}
            {club.ticketing_enabled ? (
              <Link to={`/dashboard/club/${club.club_id}/tickets`} className="block">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-12"
                  data-testid={`manage-tickets-${club.club_id}`}
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  Manage Tickets
                </Button>
              </Link>
            ) : (
              <div className="h-12" />
            )}
          </div>
          
          {/* Second row - Photo Wall */}
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Button
              size="sm"
              variant="outline"
              className="w-full border-pink-300 text-pink-700 hover:bg-pink-50 h-12"
              data-testid={`photo-wall-${club.club_id}`}
              onClick={() => openPhotoWallModal(club)}
            >
              <Camera className="w-4 h-4 mr-2" />
              Photo Wall
            </Button>
            <div className="h-12" />
            <div className="h-12" />
          </div>
        </div>
      ) : (
        // Original compact layout for non-owners (favorites)
        <>
          <div className="relative h-40 overflow-hidden">
            <img
              src={club.image_url || 'https://images.unsplash.com/photo-1761858736318-f1fe86aec4db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3VudHJ5JTIwd2VzdGVybiUyMGRhbmNlJTIwY2x1YiUyMGludGVyaW9yJTIwbmVvbnxlbnwwfHx8fDE3NzIwOTgwMjB8MA&ixlib=rb-4.1.0&q=85'}
              alt={club.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {club.is_featured && (
              <Badge className="absolute top-3 left-3 bg-violet-500 text-white">Featured</Badge>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <Link to={`/clubs/${club.slug || club.club_id}`}>
                <h3 className="font-bold text-slate-900 hover:text-violet-600 transition-colors line-clamp-1">
                  {club.name}
                </h3>
              </Link>
              <div className="flex items-center gap-0.5">
                {renderStars(club.rating)}
              </div>
            </div>
            <p className="flex items-center text-slate-400 text-sm mb-3">
              <MapPin className="w-4 h-4 mr-1" />
              {club.location || [club.city, club.region, club.country].filter(Boolean).join(', ') || 'Location not specified'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => removeFavorite(club.club_id)}
              data-testid={`remove-favorite-${club.club_id}`}
            >
              <Heart className="w-4 h-4 mr-1 fill-violet-500 text-violet-500" />
              Remove
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 lg:px-12 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Syne, sans-serif' }}>
                Dashboard
              </h1>
              <p className="text-slate-600 mt-1">
                Welcome back, {user?.name || 'Dancer'}!
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Link to="/dashboard/add-club">
                <Button className="bg-violet-600 hover:bg-violet-500 shadow-lg shadow-violet-500/25" data-testid="add-club-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Club
                </Button>
              </Link>
              <Link to="/dashboard/add-event">
                <Button variant="outline" className="border-violet-600 text-violet-700 hover:bg-violet-50" data-testid="add-event-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Add Event
                </Button>
              </Link>
              <Link to="/dashboard/add-dj">
                <Button variant="outline" className="border-violet-600 text-violet-700 hover:bg-violet-50" data-testid="add-dj-btn">
                  <Disc3 className="w-5 h-5 mr-2" />
                  Add DJ Profile
                </Button>
              </Link>
              <Link to="/dashboard/add-artist">
                <Button variant="outline" className="border-violet-600 text-violet-700 hover:bg-violet-50" data-testid="add-artist-btn">
                  <Music className="w-5 h-5 mr-2" />
                  Add Artist Profile
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        <Tabs defaultValue="my-clubs" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="my-clubs" className="flex items-center gap-2" data-testid="my-clubs-tab">
              <Music className="w-4 h-4" />
              My Clubs ({myClubs.length})
            </TabsTrigger>
            <TabsTrigger value="my-events" className="flex items-center gap-2" data-testid="my-events-tab">
              <Calendar className="w-4 h-4" />
              My Events ({myEvents.length})
            </TabsTrigger>
            <TabsTrigger value="my-djs" className="flex items-center gap-2" data-testid="my-djs-tab">
              <Disc3 className="w-4 h-4" />
              My DJ Profiles ({myDJs.length})
            </TabsTrigger>
            <TabsTrigger value="my-artists" className="flex items-center gap-2" data-testid="my-artists-tab">
              <Music className="w-4 h-4" />
              My Artists ({myArtists.length})
            </TabsTrigger>
            <TabsTrigger value="instructor" className="flex items-center gap-2" data-testid="instructor-tab">
              <Users className="w-4 h-4" />
              Instructor
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-2" data-testid="favorites-tab">
              <Heart className="w-4 h-4" />
              Favorites ({favorites.length})
            </TabsTrigger>
            {(myClubs.length > 0 || myDJs.length > 0) && (
              <TabsTrigger value="dj-analytics" className="flex items-center gap-2" data-testid="dj-analytics-tab">
                <BarChart3 className="w-4 h-4" />
                DJ Analytics
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="settings-tab">
              <Settings className="w-4 h-4" />
              Account
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-clubs">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden">
                    <div className="h-40 bg-slate-200 animate-pulse" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : myClubs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Music className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No clubs yet</h3>
                <p className="text-slate-400 mb-6">Start by adding your first line dance club!</p>
                <Link to="/dashboard/add-club">
                  <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-first-club-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Your Club
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {myClubs.map(club => (
                  <ClubCard key={club.club_id} club={club} isOwner />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-events">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden">
                    <div className="h-40 bg-slate-200 animate-pulse" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : myClubs.length === 0 && myDJs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Create a club or DJ profile first</h3>
                <p className="text-slate-400 mb-6">You need to create a club or DJ profile before adding events</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/dashboard/add-club">
                    <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-club-for-events-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      Add Club
                    </Button>
                  </Link>
                  <Button variant="outline" onClick={() => setActiveTab('my-djs')}>
                    <Disc3 className="w-5 h-5 mr-2" />
                    Add DJ Profile
                  </Button>
                </div>
              </div>
            ) : myEvents.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No events yet</h3>
                <p className="text-slate-400 mb-6">Create your first event to get dancers excited!</p>
                <Link to="/dashboard/add-event">
                  <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-first-event-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Event
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Copy All Events Button */}
                <div className="mb-4 flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={copyAllEventsToClipboard}
                    className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All for Facebook ({myEvents.length})
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myEvents.map(event => (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 ${isEventPast(event.event_date) ? 'opacity-60' : ''}`}
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={event.image_url || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800'}
                        alt={event.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      {isEventPast(event.event_date) && (
                        <Badge className="absolute top-3 left-3 bg-slate-500 text-white">Past Event</Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-slate-900 line-clamp-1 mb-1">
                        {event.title}
                      </h3>
                      <p className="text-sm text-violet-600 font-medium mb-2">{event.club_name}</p>
                      <div className="flex items-center text-slate-400 text-sm mb-1">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatEventDate(event.event_date)}
                      </div>
                      <div className="flex items-center text-slate-400 text-sm mb-3">
                        <Music className="w-4 h-4 mr-1" />
                        {formatEventTime(event.event_time)}
                        {event.price && <span className="ml-2">• £{event.price.toFixed(2)}</span>}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => copyEventToClipboard(event)}
                          title="Copy for Facebook"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/dashboard/edit-event/${event.event_id}`)}
                          data-testid={`edit-event-${event.event_id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-event-${event.event_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Event</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{event.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteEvent(event.event_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="my-djs">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden">
                    <div className="h-40 bg-slate-200 animate-pulse" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : myDJs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Disc3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No DJ profiles yet</h3>
                <p className="text-slate-400 mb-6">Create your DJ profile to get booked!</p>
                <Link to="/dashboard/add-dj">
                  <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-first-dj-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Create DJ Profile
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myDJs.map(dj => (
                  <motion.div
                    key={dj.dj_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative h-40 overflow-hidden bg-gradient-to-br from-violet-600 to-purple-700">
                      {dj.image_url ? (
                        <img src={dj.image_url} alt={dj.stage_name || dj.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Disc3 className="w-16 h-16 text-white/30" />
                        </div>
                      )}
                      {dj.is_featured && (
                        <Badge className="absolute top-3 left-3 bg-violet-500 text-white">Featured</Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <Link to={`/djs/${dj.dj_id}`}>
                        <h3 className="font-bold text-slate-900 hover:text-violet-600 transition-colors line-clamp-1">
                          {dj.stage_name || dj.name}
                        </h3>
                      </Link>
                      <p className="flex items-center text-slate-400 text-sm mt-1 mb-3">
                        <MapPin className="w-4 h-4 mr-1" />
                        {dj.location || [dj.location_city, dj.location_state].filter(Boolean).join(', ') || 'Location not specified'}
                      </p>
                      
                      {/* DJ Live Console Button - shows when DJ has dj_live_enabled */}
                      {dj.dj_live_enabled && (
                        <div className="mb-3">
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={() => navigate(`/dj/${dj.slug || dj.dj_id}/console`)}
                            data-testid={`dj-console-${dj.dj_id}`}
                          >
                            <Music className="w-4 h-4 mr-1" />
                            DJ Console
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/dashboard/edit-dj/${dj.dj_id}`)}
                          data-testid={`edit-dj-${dj.dj_id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-dj-${dj.dj_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete DJ Profile</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this DJ profile? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDJ(dj.dj_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Artists Tab */}
          <TabsContent value="my-artists">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : myArtists.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Music className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Artist Profiles</h3>
                <p className="text-slate-400 mb-6">Create an artist/performer profile to appear in our directory</p>
                <Link to="/dashboard/add-artist">
                  <Button className="bg-violet-600 hover:bg-violet-500">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Artist Profile
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {myArtists.map(artist => (
                  <motion.div
                    key={artist.artist_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl p-6 flex items-center justify-between shadow-sm"
                  >
                    <div className="flex items-center gap-4">
                      {artist.image_url ? (
                        <img src={artist.image_url} alt={artist.name} className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Music className="w-8 h-8 text-violet-500" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800">{artist.stage_name || artist.name}</h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {artist.location}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {artist.is_approved ? (
                            <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                              <Check className="w-3 h-3 mr-1" /> Approved
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                              <Clock className="w-3 h-3 mr-1" /> Pending Approval
                            </Badge>
                          )}
                          {artist.is_featured && (
                            <Badge className="bg-amber-500 text-white">Featured</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {artist.is_approved && (
                        <Link to={`/artists/${artist.slug}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                      <Link to={`/dashboard/edit-artist/${artist.artist_id}`}>
                        <Button variant="ghost" size="sm">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Instructor Tab */}
          <TabsContent value="instructor">
            <div className="space-y-6">
              {/* Instructor Profile Section */}
              {!instructorProfile ? (
                <div className="text-center py-16 bg-white rounded-2xl">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Become an Instructor</h3>
                  <p className="text-slate-400 mb-6 max-w-md mx-auto">
                    Register as a line dance instructor to appear in our directory and link to clubs where you teach.
                  </p>
                  <Button 
                    onClick={() => {
                      setInstructorFormData({
                        name: user?.name || '',
                        bio: '',
                        specialties: [],
                        specialtiesText: '',
                        email: user?.email || '',
                        phone: '',
                        years_experience: '',
                        social_links: { facebook: '', instagram: '', twitter: '', website: '' }
                      });
                      setShowInstructorForm(true);
                    }}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="become-instructor-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Register as Instructor
                  </Button>
                </div>
              ) : (
                <>
                  {/* Profile Card */}
                  <div className="bg-white rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-900">Your Instructor Profile</h3>
                      <div className="flex gap-2">
                        <Link to={`/instructors/${instructorProfile.instructor_id}`}>
                          <Button variant="outline" size="sm">View Public Profile</Button>
                        </Link>
                        <Button size="sm" onClick={openEditInstructorForm} data-testid="edit-instructor-btn">
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Avatar className="w-20 h-20">
                        <AvatarImage src={instructorProfile.image_url} />
                        <AvatarFallback className="bg-violet-100 text-violet-600 text-xl font-bold">
                          {instructorProfile.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-900">{instructorProfile.name}</h4>
                        {instructorProfile.years_experience && (
                          <p className="text-sm text-violet-600 flex items-center mt-1">
                            <Clock className="w-4 h-4 mr-1" />
                            {instructorProfile.years_experience} years experience
                          </p>
                        )}
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{instructorProfile.bio}</p>
                        {instructorProfile.specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {instructorProfile.specialties.map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Linked Clubs */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Linked Clubs</h3>
                    {instructorProfile.clubs?.length > 0 ? (
                      <div className="space-y-3 mb-6">
                        {instructorProfile.clubs.map(club => (
                          <div key={club.club_id} className="p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <MapPin className="w-5 h-5 text-violet-500" />
                                <div>
                                  <Link to={`/clubs/${club.slug || club.club_id}`} className="font-medium text-slate-900 hover:text-violet-600">
                                    {club.name}
                                  </Link>
                                  <p className="text-sm text-slate-400">{club.location}</p>
                                </div>
                              </div>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                    <X className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Unlink from Club</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to unlink from {club.name}? You'll need to send a new request to link again.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleUnlinkFromClub(club.club_id)} className="bg-red-600 hover:bg-red-700">
                                      Unlink
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                            {/* DJ Live & Bingo buttons for linked instructors */}
                            <div className="flex gap-2 mt-2">
                              {club.dj_live_enabled && (
                                <Link to={`/dj-live/${club.club_id}`} className="flex-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-pink-300 text-pink-700 hover:bg-pink-50"
                                    data-testid={`instructor-dj-console-${club.club_id}`}
                                  >
                                    <Music className="w-4 h-4 mr-1" />
                                    DJ Console
                                  </Button>
                                </Link>
                              )}
                              {club.bingo_enabled && (
                                <Link to={`/bingo/host?club=${club.club_id}`} className="flex-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-violet-300 text-violet-700 hover:bg-violet-50"
                                    data-testid={`instructor-host-bingo-${club.club_id}`}
                                  >
                                    <Gamepad2 className="w-4 h-4 mr-1" />
                                    Host Bingo
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 mb-4">You're not linked to any clubs yet. Send a request to clubs where you teach.</p>
                    )}

                    {/* Request to Link */}
                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="font-medium text-slate-700 mb-3">Request to Link to a Club</h4>
                      <div className="flex gap-3">
                        <Select value={selectedClubForLink} onValueChange={setSelectedClubForLink}>
                          <SelectTrigger className="flex-1" data-testid="select-club-for-link">
                            <SelectValue placeholder="Select a club..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allClubs
                              .filter(c => !instructorProfile.clubs?.some(lc => lc.club_id === c.club_id))
                              .filter(c => !myLinkRequests.some(r => r.club_id === c.club_id && r.status === 'pending'))
                              .map(club => (
                                <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button onClick={handleRequestClubLink} disabled={!selectedClubForLink} data-testid="request-link-btn">
                          Send Request
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Pending Requests */}
                  {myLinkRequests.filter(r => r.status === 'pending').length > 0 && (
                    <div className="bg-white rounded-2xl p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Pending Link Requests</h3>
                      <div className="space-y-3">
                        {myLinkRequests.filter(r => r.status === 'pending').map(request => (
                          <div key={request.request_id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <Clock className="w-5 h-5 text-yellow-600" />
                              <div>
                                <p className="font-medium text-slate-900">{request.club_name}</p>
                                <p className="text-sm text-slate-400">Awaiting approval</p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleCancelLinkRequest(request.request_id)}>
                              Cancel
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Club Owner: Incoming Instructor Requests */}
              {myClubs.length > 0 && Object.values(clubLinkRequests).some(reqs => reqs.some(r => r.status === 'pending')) && (
                <div className="bg-white rounded-2xl p-6 border-2 border-violet-200">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-violet-500" />
                    Instructor Requests for Your Clubs
                  </h3>
                  {myClubs.map(club => {
                    const requests = clubLinkRequests[club.club_id]?.filter(r => r.status === 'pending') || [];
                    if (requests.length === 0) return null;
                    return (
                      <div key={club.club_id} className="mb-4 last:mb-0">
                        <h4 className="font-medium text-slate-700 mb-2">{club.name}</h4>
                        <div className="space-y-2">
                          {requests.map(request => (
                            <div key={request.request_id} className="flex items-center justify-between p-3 bg-violet-50 rounded-xl">
                              <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10">
                                  <AvatarImage src={request.instructor?.image_url} />
                                  <AvatarFallback className="bg-violet-100 text-violet-600 text-sm font-bold">
                                    {request.instructor_name?.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-slate-900">{request.instructor_name}</p>
                                  <p className="text-sm text-slate-400 line-clamp-1">{request.instructor?.bio}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => handleApproveLinkRequest(club.club_id, request.request_id)}
                                  className="bg-green-600 hover:bg-green-500"
                                  data-testid={`approve-request-${request.request_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleRejectLinkRequest(club.club_id, request.request_id)}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`reject-request-${request.request_id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="favorites">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden">
                    <div className="h-40 bg-slate-200 animate-pulse" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No favorites yet</h3>
                <p className="text-slate-400 mb-6">Browse clubs and save your favorites!</p>
                <Link to="/clubs">
                  <Button className="bg-violet-600 hover:bg-violet-500" data-testid="browse-clubs-btn">
                    Browse Clubs
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map(club => (
                  <ClubCard key={club.club_id} club={club} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* DJ Analytics Tab */}
          <TabsContent value="dj-analytics">
            <TopRequestedDances />
          </TabsContent>

          <TabsContent value="settings">
            <div className="max-w-xl space-y-6">
              {/* Push Notifications Settings */}
              <NotificationSettings authToken={localStorage.getItem('token')} />
              
              {/* Change Password Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Lock className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Change Password</h3>
                    <p className="text-sm text-slate-400">Update your account password</p>
                  </div>
                </div>
                
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        placeholder="Enter current password"
                        required
                        className="pr-10"
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        placeholder="Enter new password (min 6 characters)"
                        required
                        minLength={6}
                        className="pr-10"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      placeholder="Confirm new password"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-violet-600 hover:bg-violet-500"
                    disabled={changingPassword}
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </form>
                
                <p className="text-xs text-slate-400 mt-4 text-center">
                  Note: Google sign-in users cannot change their password here
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Instructor Form Modal */}
      {showInstructorForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {instructorProfile ? 'Edit Instructor Profile' : 'Register as Instructor'}
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Fill in your details to appear in our instructor directory
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowInstructorForm(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={handleInstructorSubmit} className="p-6 space-y-4">
              {/* Profile Photo Upload */}
              <div>
                <ImageUpload
                  label="Profile Photo"
                  value={instructorFormData.image_url}
                  onChange={(url) => setInstructorFormData({ ...instructorFormData, image_url: url })}
                  helpText="Upload a professional photo for your instructor profile"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <Input
                    value={instructorFormData.name}
                    onChange={(e) => setInstructorFormData({ ...instructorFormData, name: e.target.value })}
                    placeholder="Your full name"
                    data-testid="instructor-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Years of Experience</label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={instructorFormData.years_experience}
                    onChange={(e) => setInstructorFormData({ ...instructorFormData, years_experience: e.target.value })}
                    placeholder="e.g., 5"
                    data-testid="instructor-years-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bio *</label>
                <textarea
                  value={instructorFormData.bio}
                  onChange={(e) => setInstructorFormData({ ...instructorFormData, bio: e.target.value })}
                  placeholder="Tell us about yourself, your teaching style, and experience..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                  data-testid="instructor-bio-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Specialties</label>
                <Input
                  value={instructorFormData.specialtiesText ?? instructorFormData.specialties.join(', ')}
                  onChange={(e) => setInstructorFormData({ 
                    ...instructorFormData, 
                    specialtiesText: e.target.value,
                    specialties: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                  })}
                  placeholder="e.g., Beginner Classes, Line Dance, Country Western (comma-separated)"
                  data-testid="instructor-specialties-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={instructorFormData.email}
                    onChange={(e) => setInstructorFormData({ ...instructorFormData, email: e.target.value })}
                    placeholder="your@email.com"
                    data-testid="instructor-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <Input
                    value={instructorFormData.phone}
                    onChange={(e) => setInstructorFormData({ ...instructorFormData, phone: e.target.value })}
                    placeholder="Your contact number"
                    data-testid="instructor-phone-input"
                  />
                </div>
              </div>

              {/* Social Links */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Social Media Links</label>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Facebook className="w-5 h-5 text-blue-600" />
                    <Input
                      value={instructorFormData.social_links?.facebook || ''}
                      onChange={(e) => setInstructorFormData({ 
                        ...instructorFormData, 
                        social_links: { ...instructorFormData.social_links, facebook: e.target.value }
                      })}
                      placeholder="Facebook URL"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Instagram className="w-5 h-5 text-pink-600" />
                    <Input
                      value={instructorFormData.social_links?.instagram || ''}
                      onChange={(e) => setInstructorFormData({ 
                        ...instructorFormData, 
                        social_links: { ...instructorFormData.social_links, instagram: e.target.value }
                      })}
                      placeholder="Instagram URL"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Twitter className="w-5 h-5 text-sky-500" />
                    <Input
                      value={instructorFormData.social_links?.twitter || ''}
                      onChange={(e) => setInstructorFormData({ 
                        ...instructorFormData, 
                        social_links: { ...instructorFormData.social_links, twitter: e.target.value }
                      })}
                      placeholder="Twitter URL"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-slate-600" />
                    <Input
                      value={instructorFormData.social_links?.website || ''}
                      onChange={(e) => setInstructorFormData({ 
                        ...instructorFormData, 
                        social_links: { ...instructorFormData.social_links, website: e.target.value }
                      })}
                      placeholder="Website URL"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" variant="outline" onClick={() => setShowInstructorForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={instructorSubmitting} className="bg-violet-600 hover:bg-violet-500" data-testid="submit-instructor-btn">
                  {instructorSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (instructorProfile ? 'Save Changes' : 'Register')}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      
      {/* Photo Wall Modal */}
      {photoWallModalClub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Camera className="w-6 h-6 text-white" />
                  <h2 className="text-xl font-bold text-white">Photo Wall</h2>
                </div>
                <button
                  onClick={() => setPhotoWallModalClub(null)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-white/80 text-sm mt-1">{photoWallModalClub.name}</p>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {photoWallLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : photoWallSession ? (
                /* Active Session */
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Session Active
                    </div>
                    <p className="text-green-800 font-semibold">{photoWallSession.session_name}</p>
                    <p className="text-green-600 text-sm">
                      Code: <span className="font-mono font-bold">{photoWallSession.session_code}</span>
                    </p>
                    <p className="text-green-600 text-sm">
                      {photoWallSession.photo_count || 0} photos uploaded
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Link 
                      to={`/club/${photoWallModalClub.slug || photoWallModalClub.club_id}/photo-wall`}
                      className="block"
                    >
                      <Button variant="outline" className="w-full border-pink-300 text-pink-600">
                        <Camera className="w-4 h-4 mr-2" />
                        Upload Page
                      </Button>
                    </Link>
                    <Link 
                      to={`/club/${photoWallModalClub.slug || photoWallModalClub.club_id}/photo-wall/display`}
                      className="block"
                    >
                      <Button variant="outline" className="w-full border-purple-300 text-purple-600">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Slideshow
                      </Button>
                    </Link>
                  </div>
                  
                  <Button 
                    onClick={endPhotoWallSession}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                  >
                    End Session
                  </Button>
                </div>
              ) : (
                /* No Active Session */
                <div className="space-y-4">
                  <p className="text-slate-600 text-center">
                    Start a Photo Wall session for your event. Guests can scan a QR code to upload photos that appear on a live slideshow.
                  </p>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Session Name</label>
                    <Input
                      value={photoWallSessionName}
                      onChange={(e) => setPhotoWallSessionName(e.target.value)}
                      placeholder="e.g., Friday Night Party"
                    />
                  </div>
                  
                  <Button 
                    onClick={startPhotoWallSession}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                    disabled={!photoWallSessionName.trim()}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Photo Wall
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
